<script lang="ts">
  import { Button } from "$lib/components/ui/button";
  import LoadingIcon from "$src/presentation/defaults/loading-icon.svelte";
  import * as Table from "$lib/components/ui/table/index.js";
  import { t } from "svelte-i18n";
  import { FolderOpen } from "lucide-svelte";
  import * as Card from "$lib/components/ui/card";
  import { CircleChevronLeft } from "lucide-svelte";
  import { languageCode } from "$src/presentation/core/languageCode";
  import { getTranslation } from "$src/presentation/core/TranslatedTextUtils";
  import { DateTime } from "luxon";
  import { ScrollArea } from "$lib/components/ui/scroll-area";
  import { push } from "svelte-spa-router";
  import { useCustomerProjects } from "$src/presentation/customers/hooks/useCustomerProjects";
  import { writable } from "svelte/store";
  import { type Customer } from "$src/domain/customers/Customer";
  import { getCustomerInfo } from "$src/domain/customers/utils/customer-info-utils";
  import Badge from "$lib/components/ui/badge/badge.svelte";

  export let customer: Customer | null;
  export let onBack;

  export let groupStoreId: number;

  const defaultPage = 1;
  const currentPage = writable(defaultPage);

  $: queryCustomerProjects = useCustomerProjects(
    groupStoreId,
    customer?.id ?? -1,
    $currentPage
  );

  function navigateToDetails(projectId: number) {
    push(`/gs/${groupStoreId}/projects/${projectId}`);
  }
</script>

<Card.Root class="flex flex-col h-full max-h-full justify-center">
  <Card.Header>
    <Card.Title>
      <div class="flex items-center w-full">
        {#if onBack}
          <div class="md:hidden pr-2">
            <Button size="sm" class="h-7 gap-1" on:click={onBack}>
              <CircleChevronLeft class="h-3.5 w-3.5" />
              <span class=" sm:whitespace-nowrap"> Retour </span>
            </Button>
          </div>
        {/if}
        <div class="flex-grow text-center">
          {#if customer}
            {$t("page.projects.index.projects-title")} : {getCustomerInfo(
              customer
            )}
          {:else}
            {$t("page.projects.index.projects-title")}
          {/if}
        </div>
      </div>
    </Card.Title>
  </Card.Header>
  <Card.Content class="flex-grow overflow-hidden">
    <ScrollArea orientation="vertical" class="h-full ">
      {#if customer}
        {#if $queryCustomerProjects.isLoading}
          <LoadingIcon />
        {:else if $queryCustomerProjects.data}
          {#if Object.keys($queryCustomerProjects.data.items).length > 0}
            <Table.Root>
              <Table.Header class="sticky top-0 bg-secondary mt-0">
                <Table.Row>
                  <Table.Head class="w-[100px]">N°</Table.Head>
                  <Table.Head>Statut</Table.Head>
                  <Table.Head>Version</Table.Head>
                  <Table.Head class="hidden md:table-cell">Vendeur</Table.Head>
                  <Table.Head class="hidden sm:table-cell">Créé le</Table.Head>
                  <Table.Head class="hidden xl:table-cell">Magasin</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {#each $queryCustomerProjects.data.items as group (group.folder.id)}
                  <Table.Row class="hover:bg-background font-medium">
                    <Table.Cell
                      colspan={6}
                      class="text-xl table-cell justify-center gap-2"
                    >
                      <div class="flex flex-row items-center gap-2">
                        <FolderOpen class="h-5 w-5" />
                        {group.folder.name}
                        {#if group.folder.type}
                          <Badge variant="secondary">
                            {getTranslation(
                              group.folder.type.translations,
                              $languageCode
                            )}
                          </Badge>
                        {/if}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                  {#each group.projects as project (project.id)}
                    <Table.Row
                      class="cursor-pointer"
                      on:click={() => navigateToDetails(project.id)}
                    >
                      <Table.Cell class="font-medium flex-none">
                        {project.id}
                      </Table.Cell>
                      <Table.Cell>
                        {#if project.currentStep}
                          {getTranslation(
                            project.currentStep.translations,
                            $languageCode
                          )}
                        {/if}
                      </Table.Cell>
                      <Table.Cell>{project.name}</Table.Cell>
                      <Table.Cell class="hidden md:table-cell"
                        >{project.sellerName}</Table.Cell
                      >
                      <Table.Cell class="hidden sm:table-cell"
                        >{DateTime.fromISO(
                          project.dateCreated
                        ).toLocaleString()}</Table.Cell
                      >
                      <Table.Cell class="hidden xl:table-cell"
                        >{project.storeName}</Table.Cell
                      >
                    </Table.Row>
                  {/each}
                {/each}
              </Table.Body>
            </Table.Root>
          {:else}
            {$t("global.messages.noresult")}
          {/if}
        {:else if $queryCustomerProjects.isError}
          <p style="color: red">{$queryCustomerProjects.error.message}</p>
        {/if}
      {:else}
        {$t("global.messages.noresult")}
      {/if}
    </ScrollArea>
  </Card.Content>
</Card.Root>
